<script>
import {
  iButton,
  iSelect,
  iTextInput,
  iToggle,
  IToggleSizes,
  showToast,
} from '@inscribe/signal';
import CountryList from 'country-list-with-dial-code-and-flag';

export default {
  components: {
    iButton,
    iSelect,
    iTextInput,
    iToggle,
  },
  data() {
    return {
      loading: false,
      intervalId: undefined,
      phone: '',
      phoneObject: {},
      registrationComplete: false,
      qrCodeUrl: undefined,
      otpCode: undefined,
      IToggleSizes,
      method: 'authyapp',
      toggleValue: true,
    };
  },
  computed: {
    isPhoneValid() {
      return (
        this.phoneObject.countryCallingCode &&
        this.phoneObject.nationalNumber?.length > 4
      );
    },
    countryOptions() {
      return CountryList.getAll().map((country) => {
        return {
          ...country,
          text: `${country.flag} ${country.name} (${country.dial_code})`,
          value: country.dial_code,
        };
      });
    },
  },
  methods: {
    finishedSetup(type) {
      this.$router.push({
        name: 'await-2fa',
        query: {
          type,
          isEnabling: true,
          ...this.$route.query,
        },
      });
    },
    enableTwoFa() {
      const data = new FormData();
      this.loading = true;

      data.append('phone_number', this.phoneObject.nationalNumber);
      data.append(
        'country_code',
        this.phoneObject.countryCallingCode.replace('+', ''),
      );
      this.$store
        .dispatch('registerTwoFa', data)
        .then((response) => {
          this.loading = false;
          this.registrationComplete = true;
          this.qrCodeUrl = response.data.qr_code;
        })
        .catch((error) => {
          this.loading = false;
          showToast({
            title: 'Error logging in',
            text: error?.response?.data?.message || 'Please try again',
            isError: true,
          });
        });
    },
    onToggleUpdate(val) {
      this.method = val ? 'authyapp' : 'generic';
    },
    onCountryCodeChanged(val) {
      this.phoneObject.countryCallingCode = val;
    },
  },
};
</script>

<template>
  <div
    class="two-fa-enable-screen auth-screen d-flex flex-column align-items-center justify-content-center i-gap-10"
  >
    <div v-if="!registrationComplete" class="form d-flex flex-column i-gap-10">
      <div class="title-reg d-flex flex-column align-items-center i-gap-10">
        <div class="i-text-heading-3 i-text-neutral-120">
          Enable Two-Factor Auth
        </div>
        <img
          src="../../img/security.svg"
          width="300px"
          class="i-mb-3 mx-auto"
        />
      </div>

      <!-- phone selector -->
      <div class="phone-selector d-flex flex-row">
        <i-select
          :options="countryOptions"
          :default-value="phoneObject.countryCallingCode"
          placeholder="Country"
          size="md"
          @option-selected="onCountryCodeChanged"
        />
        <i-text-input
          :value="phoneObject.nationalNumber"
          placeholder="Phone number"
          name="confirm-password"
          @input="phoneObject.nationalNumber = $event"
        >
        </i-text-input>
      </div>

      <div
        class="flex flex-column justify-content-start align-items-center i-mt-2"
      >
        <i-button
          id="enableTwoFaButton"
          text="Enable Two Factor Auth"
          type="primary"
          subtype="strong"
          size="lg"
          :disabled="!isPhoneValid"
          :loading="loading"
          @button-click="enableTwoFa"
        ></i-button>
      </div>
    </div>
    <div v-else class="form d-flex flex-column justify-content-start i-gap-10">
      <i-toggle
        left-label="Authy App"
        right-label="Generic Two FA"
        :toggle-value="toggleValue"
        :full-width="true"
        :size="IToggleSizes.SMALL"
        class="i-mb-3"
        @value-update="onToggleUpdate"
      ></i-toggle>

      <div
        v-if="method === 'authyapp'"
        class="title d-flex flex-column align-items-center justify-content-start i-gap-10"
      >
        <div class="i-text-heading-3 i-text-neutral-120">
          Download the Authy App
        </div>
        <div class="description i-text-body-1 i-text-neutral-110">
          Download the Authy app on your iOS or Android device, and input your
          phone number to complete the process.
        </div>

        <i-button
          text="Done"
          type="primary"
          subtype="strong"
          size="lg"
          :loading="loading"
          @button-click="() => finishedSetup('ONETOUCH')"
        ></i-button>
      </div>
      <div v-else class="title d-flex flex-column align-items-center i-gap-10">
        <div class="i-text-heading-3 i-text-neutral-120 align-items-center">
          Generic Two FA
        </div>
        <div
          class="description i-text-body-1 i-text-neutral-110 d-flex flex-column align-items-center"
        >
          Please scan the QR code below with your two-factor authentication app
          of choice, and then click the button below.
          <img width="200" height="200" :src="qrCodeUrl" />
        </div>

        <i-button
          text="Complete Setup"
          type="primary"
          subtype="strong"
          size="lg"
          :loading="loading"
          @button-click="() => finishedSetup('OTP')"
        ></i-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.two-fa-enable-screen {
  width: 800px;

  .form {
    width: 500px;
  }

  .title {
    min-height: 450px;
  }

  .description {
    text-align: center;
  }

  .phone-selector {
    .i-select {
      width: 250px;
      :deep(.selected-input),
      :deep(.items) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right: none;

        span {
          font-size: 12px;
          line-height: 18px;
          overflow: hidden;
          white-space: nowrap;
        }

        .item {
          width: 100%;
          overflow: hidden;
        }
      }
    }

    .i-text-input {
      width: 250px;
      :deep(.input-wrapper) {
        border-top-left-radius: 0 !important;
        border-bottom-left-radius: 0 !important;
      }
    }
  }
}
</style>
